// galaxy/map.js: the face-on maps of the disk, rendered once at load on the GPU.
//   mapA: rgb = old-star light (thin disk + old-star arms), a = dust optical depth
//   mapB: rgb = young-star light + HII glow (blue-white + pink), a = young-star density
//   mapC: as mapA over the inner MAP_RC kpc (bar lanes, nuclear ring) with 4x finer texels
// All cover [-R, R]^2 kpc (u -> x, v -> z), values sqrt-encoded (see ENC).
// The arm centre lines are Reid+2019 (model.js). Everything drawn around them here (widths,
// clumps, spurs, feathers, BRANCHES, bar lanes, nuclear ring) is artistic, after the look of
// NASA/JPL-Caltech/R. Hurt's Milky Way illustration and barred spirals such as NGC 1300.
import * as THREE from 'three';
import { FullScreenQuad, GLSL_NOISE } from '../lib/common.js';
import { GLSL_ARMS } from './glsl.js';
import { MAP_R, BAR_PHI, D2R, armUniforms } from './model.js';

// v_stored = sqrt(v / SCALE): works for half-float and 8-bit targets alike
export const ENC = { col: 3.0, dust: 10.0 };
export const MAP_RC = 4.5; // kpc, half-size of the inner zoom map (old light + dust over the bar and bulge)

// Artistic branches (not in Reid+2019): [arm index (model.ARMS), root beta (deg), length (deg,
// toward lower beta = outward along the arm), divergence d lnR / d(-beta) per rad (> 0 peels
// away outward at a larger pitch, < 0 curls inward as a spur), amplitude]
const BRANCHES = [
  [0, -60, 70, 0.28, 0.75], [0, -150, 60, 0.22, 0.6], [0, -200, 45, -0.18, 0.45], [0, 5, 55, 0.3, 0.5],
  [1, 150, 55, 0.25, 0.5], [1, 20, 60, 0.25, 0.6], [1, -70, 50, 0.2, 0.5], [1, 85, 50, -0.22, 0.45],
  [3, -60, 55, 0.3, 0.55], [3, 150, 50, 0.3, 0.4], [3, 50, 45, -0.25, 0.4],
  [2, -280, 55, 0.25, 0.5], [2, -150, 45, -0.2, 0.4], [2, -60, 50, 0.3, 0.45],
];
const f4 = (x) => x.toFixed(4);
const GLSL_BRANCH = `
#define NBR ${BRANCHES.length}
const vec4 BR[NBR] = vec4[NBR](${BRANCHES.map((b) => `vec4(${f4(b[0])}, ${f4(b[1] * D2R)}, ${f4(b[2] * D2R)}, ${f4(b[3])})`).join(', ')});
const float BRA[NBR] = float[NBR](${BRANCHES.map((b) => f4(b[4])).join(', ')});
`;

const FRAG = /* glsl */`
precision highp float;
varying vec2 vUv;
uniform float uMapR, uBarPhi;
uniform int uPass;
${GLSL_ARMS}
${GLSL_NOISE}
${GLSL_BRANCH}
float ridge(float n) { return 1.0 - abs(n); }
void main() {
  vec2 xz = (vUv * 2.0 - 1.0) * uMapR;
  float R = length(xz);
  float phi = atan(xz.y, xz.x);
  float beta = phi - 1.5707963;
  // bar frame: u along the near end, v toward the rotation direction
  float cb = cos(uBarPhi), sb = sin(uBarPhi);
  float bu = xz.x * cb + xz.y * sb, bv = -xz.x * sb + xz.y * cb;

  // large-scale noise fields (kpc coordinates)
  vec3 P = vec3(xz, 0.0);
  float nBig = fbm3(P * 0.22 + vec3(3.1, 7.7, 1.3), 4);
  float nMid = fbm3(P * 0.9 + vec3(11.2, 5.3, 2.9), 5);
  float nFine = fbm3(P * 3.1 + vec3(1.7, 9.1, 4.4), 5);

  float oldArm = 0.0, young = 0.0, dust = 0.0, hii = 0.0, ycl = 0.0;
  for (int i = 0; i < NARMS; i++) {
    vec2 rg = armRange(i);
    vec4 amp = uArmAmp[i];
    float w0 = uArmInfo[i].z;
    float M0 = uArmInfo[i].w;                     // 1 = major (stellar-density) arm
    float fi = float(i);
    for (int k = -2; k <= 1; k++) {
      float b = beta + 6.2831853 * float(k);
      if (b < rg.x - 0.5 || b > rg.y + 0.5) continue;
      vec2 a = armAt(i, b);
      if (a.y <= 0.0) continue;
      float Ra = exp(a.x);
      float along = b * Ra;                        // ~arc length (kpc)
      // width grows outward (Reid+2019: dw/dR = 42 pc/kpc) and swings 0.7-1.35x along the arm
      float wv = 0.7 + 0.65 * (0.5 + 0.5 * snoise(vec3(along * 0.1, fi * 3.1, 3.3)));
      float w = max(0.15, w0 + 0.042 * (Ra - 6.0)) * mix(1.0, 1.5, M0) * wv;
      float wob = 0.5 * w * snoise(vec3(along * 0.18, fi * 7.1, 0.5))
                + 0.18 * w * snoise(vec3(along * 1.1, fi * 3.3, 1.5));
      float d = R - Ra - wob;
      float PR = 6.0 * w + 2.0;
      if (abs(d) > PR) continue;
      float sg = snoise(vec3(along * 0.16, fi * 2.7, 7.0));
      float M = M0 * (1.0 - smoothstep(10.5, 13.5, Ra)); // even the majors fray into segments outside
      // bright star-forming stretches and quieter gaps (breaks the even winding)
      float sw = 0.35 + 1.0 * smoothstep(-0.6, 0.6, snoise(vec3(along * 0.075, fi * 5.3, 21.0)));
      float fade = a.y * sw * mix(0.08 + 0.92 * smoothstep(-0.2, 0.45, sg), 0.72 + 0.4 * smoothstep(-0.6, 0.6, sg), M);
      // spurs: streaks leaving the arm outward at a larger pitch (lines along + 1.6 d = const), wandering
      float s = along + 1.6 * d;
      float sp = pow(ridge(snoise(vec3(s * 1.3, d * 0.3 + fi * 5.0, 2.0))), 4.0);
      sp *= smoothstep(-0.5 * w, 0.3 * w, d) * exp(-max(d, 0.0) / (1.6 * w + 0.35))
          * smoothstep(-0.25, 0.35, snoise(vec3(s * 0.33, fi * 1.7, 9.0)));
      float clumps = 0.5 + 0.5 * snoise(vec3(along * 1.8, d * 2.2, fi * 9.0 + 4.0));
      clumps = clumps * 0.65 + 0.35 * (0.5 + 0.5 * nMid);
      oldArm += amp.x * fade * exp(-0.5 * pow(d / (1.7 * w), 2.0)) * (0.75 + 0.4 * clumps);
      float yo = exp(-0.5 * pow((d - 0.2 * w) / (0.75 * w), 2.0));
      // outer disk: the arm frays into two or three strands
      float fr = smoothstep(9.0, 12.5, Ra);
      float st1 = exp(-0.5 * pow((d + 0.6 * w) / (0.3 * w), 2.0)) * smoothstep(-0.2, 0.3, snoise(vec3(along * 0.5, fi, 31.0)));
      float st2 = exp(-0.5 * pow((d - 1.0 * w) / (0.3 * w), 2.0)) * smoothstep(-0.2, 0.3, snoise(vec3(along * 0.5, fi, 37.0)));
      yo = mix(yo, 0.45 * yo + 0.9 * (st1 + st2), fr);
      // weak inner shoulder of young light so the lane reads as a dark band, not just an edge
      float yo2 = 0.22 * exp(-0.5 * pow((d + 1.6 * w) / (0.45 * w), 2.0));
      young += amp.y * fade * ((yo + yo2) * (0.12 + 2.2 * clumps * clumps * clumps) + 0.6 * sp * (0.3 + clumps));
      // bright blue young clusters (~0.1-0.2 kpc) strung along the major arms
      float cb = pow(max(snoise(vec3(along * 2.6, d * 3.0, fi * 5.0 + 9.0)), 0.0), 3.0);
      young += amp.y * fade * (0.15 + M0) * 2.6 * cb * yo;
      ycl += amp.y * fade * (0.15 + M0) * 2.6 * cb * yo; // SPECTRUM: tinted pale cyan below
      // dust lane on the concave (inner) edge; feathers run from it across the arm, deep into the gap
      float fe = pow(ridge(snoise(vec3(s * 1.3 + 0.4, d * 0.35 + fi * 5.0, 2.0))), 3.0);
      float fe2 = pow(ridge(snoise(vec3((along + 2.4 * d) * 2.0, d * 0.6 + fi * 3.0, 13.0))), 6.0);
      float du = exp(-0.5 * pow((d + 0.9 * w) / (0.34 * w), 2.0));
      float duWide = exp(-0.5 * pow((d + 0.45 * w) / (1.0 * w), 2.0));
      float reach = smoothstep(-1.1 * w, -0.3 * w, d) * exp(-max(d, 0.0) / (1.8 * w + 0.6)) * (1.0 - smoothstep(0.5 * PR, PR, abs(d)));
      float feat = (fe + 0.8 * fe2) * reach * smoothstep(-0.3, 0.3, snoise(vec3(s * 0.25, d * 0.2, fi * 4.0 + 17.0)));
      dust += amp.z * fade * (du * (0.5 + 1.2 * fe) * 1.5 + 0.22 * duWide * (0.5 + 0.9 * fe) + 0.9 * feat);
      float hk = smoothstep(0.66, 0.95, clumps + 0.2 * nFine); // pink concentrated in knots, not a wash
      // giant HII complexes (~0.2-0.5 kpc): rarer, bigger, brighter, just downstream of the lane
      float gc = smoothstep(0.66, 0.96, 0.5 + 0.5 * snoise(vec3(along * 0.9, d * 1.2, fi * 13.0 + 3.0)));
      // complexes cluster in star-forming stretches of the arm (not evenly spaced), mostly in the inner arms
      float gs = smoothstep(-0.15, 0.55, snoise(vec3(along * 0.22, fi * 2.3, 51.0)));
      gc *= gc * (0.5 + 0.5 * ridge(nFine)) * gs * (1.0 - 0.75 * smoothstep(8.0, 12.5, Ra));
      hii += amp.w * fade * (1.3 * hk * (yo + 0.4 * sp) + 3.0 * gc * exp(-0.5 * pow((d - 0.25 * w) / (1.0 * w), 2.0)));
    }
  }
  // artistic branches: thinner, segmented arms peeling off at a larger pitch
  for (int j = 0; j < NBR; j++) {
    int i = int(BR[j].x);
    float b0 = BR[j].y, bl = BR[j].z, kd = BR[j].w;
    float fj = float(j);
    for (int k = -2; k <= 1; k++) {
      float b = beta + 6.2831853 * float(k);
      if (b > b0 + 0.02 || b < b0 - bl) continue;
      float s = (b0 - b) / bl;                     // 0 at the root, 1 at the tip
      float Rb = exp(armAt(i, b).x + kd * (b0 - b));
      float wb = max(0.12, 0.75 * (uArmInfo[i].z + 0.042 * (Rb - 6.0)));
      float al = b * Rb;
      float d = R - Rb - 0.5 * wb * snoise(vec3(al * 0.45, fj * 3.7, 11.0));
      if (abs(d) > 5.0 * wb + 0.4) continue;
      float env = 2.3 * BRA[j] * smoothstep(0.0, 0.12, s) * (1.0 - smoothstep(0.5, 1.0, s))
                * smoothstep(-0.35, 0.35, snoise(vec3(al * 0.4, fj * 1.9, 5.0)));
      float cl = 0.5 + 0.5 * snoise(vec3(al * 2.1, d * 2.0, fj * 7.0 + 2.0));
      float yb = exp(-0.5 * pow(d / (0.8 * wb), 2.0));
      young += env * yb * (0.1 + 2.0 * cl * cl * cl);
      oldArm += 0.25 * env * exp(-0.5 * pow(d / (1.5 * wb), 2.0));
      dust += 0.7 * env * exp(-0.5 * pow((d + 0.9 * wb) / (0.4 * wb), 2.0)) * (0.6 + 0.8 * cl);
      hii += 0.9 * env * yb * smoothstep(0.62, 0.92, cl + 0.2 * nFine);
    }
  }

  // radial envelopes
  float oldDisk = exp(-R / 2.6) * (1.0 - 0.4 * exp(-pow(R / 3.0, 4.0))); // inner disk depleted inside the bar
  // ragged outer edge (the star-forming disk ends at 13-17 kpc, varying with azimuth)
  float sfr = smoothstep(2.6, 4.4, R) * exp(-max(R - 5.0, 0.0) / 4.0) * (1.0 - smoothstep(12.8 + 2.2 * nBig, 16.8 + 1.2 * nBig, R));
  float dustEnv = smoothstep(2.4, 4.0, R) * exp(-max(R - 5.0, 0.0) / 5.0) * (1.0 - smoothstep(15.0, 17.5, R));
  // flocculent inter-arm star formation + small young clusters (~0.2 kpc)
  float flocc = smoothstep(0.15, 0.7, 0.5 + 0.5 * nMid) * (0.5 + 0.5 * nBig);
  float kn = pow(max(snoise(vec3(xz * 4.3, 21.0)), 0.0), 3.0);

  // the bar region: thin, crisp, double-stranded lanes on the bar's leading edges (cf. NGC 1300)
  float barLen = 4.6;
  float inBar = 1.0 - smoothstep(barLen - 0.6, barLen + 0.3, abs(bu));
  float lc = bv - sign(bu) * (0.36 + 0.08 * abs(bu) + 0.025 * bu * bu);
  float lw = 0.04 + 0.012 * abs(bu);
  float lsp = snoise(vec3(bu * 1.9, 3.0, 1.0));
  float lane = exp(-0.5 * pow(lc / lw, 2.0)) * (0.55 + 0.6 * smoothstep(-0.5, 0.4, lsp))
             + 0.5 * exp(-0.5 * pow((lc + sign(bu) * (0.12 + 0.05 * lsp)) / (0.6 * lw), 2.0)) * smoothstep(-0.2, 0.5, snoise(vec3(bu * 2.6, 7.0, 2.0)))
             + 0.22 * exp(-0.5 * pow(lc / (3.5 * lw), 2.0));
  float lf = pow(ridge(snoise(vec3(bu * 3.2 + lc * 5.0, lc * 1.5, 4.0))), 6.0) * exp(-abs(lc) / 0.25); // feathers off the lanes
  lane = (lane + 0.35 * lf) * smoothstep(0.25, 1.0, abs(bu)) * inBar;
  float cmzR = length(vec2(bu / 1.0, bv / 0.45));
  float cmz = (exp(-0.5 * pow((cmzR - 0.2) / 0.12, 2.0)) + 0.4 * exp(-cmzR / 0.1)) * (0.4 + 0.8 * (0.5 + 0.5 * nFine));
  // nuclear ring (the CMZ's ring of dense gas; ~0.3 x 0.2 kpc here, artistic): thin, clumpy
  float rr = length(vec2(bu / 0.3, bv / 0.19));
  float ring = exp(-0.5 * pow((rr - 1.0) / 0.1, 2.0)) * (0.2 + 1.1 * smoothstep(-0.3, 0.6, snoise(vec3(xz * 9.0, 3.0))))
             + 0.35 * exp(-0.5 * pow((rr - 1.3) / 0.06, 2.0)) * smoothstep(0.0, 0.6, snoise(vec3(xz * 7.0, 8.0)));
  // bar ends (ansae): where the arms start, gas piles up
  float ansae = exp(-0.5 * (pow((abs(bu) - 4.3) / 0.45, 2.0) + pow(bv / 0.5, 2.0)));

  if (uPass == 0) {
    // old stars: warm, inner disk warmer; arms slightly brighter in old light too
    float L = oldDisk * (0.28 + 1.6 * oldArm) * (0.85 + 0.3 * nBig);
    L += 0.075 * exp(-R / 5.0) * (0.75 + 0.25 * (1.0 + nBig)); // faint outer old disk: a continuous glow the arms sit in
    L *= exp(-0.3 * dust * dustEnv); // lanes + feathers carve the arm light (so they read face-on too)
    // SPECTRUM (representative colour): pale-cream bar/bulge light -> lavender-white outer disk, never amber;
    // luminance kept as before (same exposure, same star acceptance)
    vec3 cOld = mix(vec3(1.0, 0.9, 0.78), vec3(0.36, 0.6, 1.0), smoothstep(1.0, 5.0, R));
    cOld *= mix(0.678, 0.825, smoothstep(2.0, 9.0, R)) / dot(cOld, vec3(0.2126, 0.7152, 0.0722));
    float tau = dust * dustEnv * (0.55 + 0.9 * (0.5 + 0.5 * nMid)) * 2.6;
    tau += 0.35 * dustEnv * (0.4 + 0.6 * (0.5 + 0.5 * nMid)) * smoothstep(0.2, 0.9, 0.5 + 0.5 * nFine); // diffuse disk dust
    tau += 2.4 * lane + 0.5 * cmz + 2.8 * ring + 0.6 * ansae;
    // inner disk: a light patchy veil + thin filaments (crisp in silhouette against the bulge)
    float inner = smoothstep(0.25, 1.1, R) * (1.0 - smoothstep(3.2, 4.6, R));
    // stretched ~2.5:1 along the orbits (no radial streaks toward the camera), domain-warped and broken
    // into pieces (no concentric 'record grooves')
    float fil = pow(ridge(snoise(vec3(R * 3.2 + 1.4 * nMid, xz * 1.3))), 7.0) + 0.6 * pow(ridge(snoise(vec3(R * 7.0 + 1.0 * nFine, xz * 2.8 + 4.0))), 9.0);
    fil *= smoothstep(-0.3, 0.4, snoise(vec3(xz * 0.9, 41.0)));
    tau += inner * (0.1 + 0.45 * smoothstep(0.5, 0.9, 0.5 + 0.5 * nMid) + 1.6 * fil * (0.4 + 0.6 * smoothstep(-0.2, 0.5, nMid)));
    tau *= 1.0 - smoothstep(15.0, 17.5, R);
    vec3 col = cOld * L;
    gl_FragColor = vec4(sqrt(max(col, 0.0) / ${ENC.col.toFixed(1)}), sqrt(max(tau, 0.0) / ${ENC.dust.toFixed(1)}));
  } else {
    float Y = (young * (0.75 + 0.9 * kn) + 0.14 * flocc * (0.3 + 1.4 * kn)) * sfr + 0.5 * ansae + 0.3 * cmz;
    // HII regions concentrate in the inner arms and thin out beyond the solar circle (artistic weights)
    float H = hii * sfr * 3.8 * mix(1.2, 0.4, smoothstep(7.0, 13.0, R)) + 0.8 * cmz * (0.5 + 0.5 * nFine) + 0.3 * ansae;
    float dcv = dust * dustEnv;
    Y *= exp(-0.75 * dcv); H *= exp(-0.4 * dcv);
    // mild lopsidedness (m = 1) in the outer disk: real disks are rarely symmetric (artistic)
    float lop = 1.0 + 0.22 * cos(phi - 2.4) * smoothstep(5.0, 10.0, R);
    Y *= lop; H *= lop;
    // SPECTRUM: young-star blue (#3D7BFF; bright clusters whiten toward #8FD3FF in the tone map), HII hot pink (#FF4FA3)
    vec3 cYoung = vec3(0.045, 0.32, 1.35);
    vec3 cHii = vec3(1.0, 0.1, 0.45);
    vec3 cY = mix(cYoung, vec3(0.28, 0.78, 1.15), clamp(1.8 * ycl / max(young, 1e-4), 0.0, 1.0)); // clusters: #8FD3FF
    vec3 col = cY * Y * 1.3 + cHii * H * 1.9;
    gl_FragColor = vec4(sqrt(max(col, 0.0) / ${ENC.col.toFixed(1)}), sqrt(clamp(Y * 0.5, 0.0, 1.0)));
  }
}`;

export function buildMaps(renderer, { size = 1024, half = true } = {}) {
  const mk = () => {
    const rt = new THREE.WebGLRenderTarget(size, size, {
      type: half ? THREE.HalfFloatType : THREE.UnsignedByteType,
      depthBuffer: false,
      generateMipmaps: true,
      minFilter: THREE.LinearMipmapLinearFilter,
      magFilter: THREE.LinearFilter,
      wrapS: THREE.ClampToEdgeWrapping,
      wrapT: THREE.ClampToEdgeWrapping,
    });
    return rt;
  };
  const A = mk(), B = mk(), C = mk();
  const mat = new THREE.ShaderMaterial({
    uniforms: { uMapR: { value: MAP_R }, uBarPhi: { value: BAR_PHI }, uPass: { value: 0 }, ...armUniforms() },
    vertexShader: 'varying vec2 vUv; void main(){ vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }',
    fragmentShader: FRAG,
    depthTest: false, depthWrite: false,
  });
  const q = new FullScreenQuad(mat);
  const prev = renderer.getRenderTarget();
  mat.uniforms.uPass.value = 0;
  renderer.setRenderTarget(A); q.render(renderer);
  mat.uniforms.uPass.value = 1;
  renderer.setRenderTarget(B); q.render(renderer);
  mat.uniforms.uPass.value = 0; mat.uniforms.uMapR.value = MAP_RC;
  renderer.setRenderTarget(C); q.render(renderer);
  renderer.setRenderTarget(prev);
  mat.dispose(); q.dispose();
  return { A, B, C, size, dispose() { A.dispose(); B.dispose(); C.dispose(); } };
}
